/**
 * VariationPreviewModal — 아직 은행에 없는 산출(변형 ⑤ · 생성 ⑥)의 **지면 모양 미리보기**.
 *
 * [문제 보기] 탭의 [미리보기]와 같은 자리·같은 얼굴이다. 다른 것은 **받는 길** 하나뿐 —
 * 그쪽은 문제 번호로 서버에서 본문을 받아 오는데(ProblemPreviewV2), 검수 중인 산출에는
 * 아직 번호가 없다. 그래서 본문만 밖에서 받고, **그리는 부품은 그대로 쓴다**
 * (ProblemBlock · ProblemBodyPreview · ConceptBadge · 개념 사전 · 설명 모달).
 * 마크업을 복제하지 않는 이유는 늘 같다 — 두 벌이 되면 한쪽만 고쳐도 빌드는 통과하고
 * 화면도 떠서, 같은 문제가 자리에 따라 다른 조판으로 그려진다.
 *
 * 왜 필요한가: 검수 카드의 편집기는 **화면 폭**으로 그린다. 지면은 한 단 423px·글자 18px 라
 * 줄이 다른 자리에서 꺾이고 선지 격자도 달라진다. 「이대로 등록하면 종이에 어떻게 앉는가」는
 * 그 폭으로 봐야 알 수 있다.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, MathceptDetailModal } from '../common';
import { useAuth } from '../../context/AuthContext';
import { ProblemBlock } from '../ExamPapersV2/ProblemPreviewV2';
import { fetchConceptDictV2, fetchConceptDetailV2 } from '../ExamPapersV2/conceptBadge';
import { loadLexicalToHtml, parseDoc } from '../ExamPapersV2/lexicalHtml';

/**
 * @param bodyLexical  payloadToLexical 로 굳힌 본문 editorState 문자열
 * @param conceptNames [{concept_id, name}] — 검수에서 고친 값이 반영된 목록
 */
function VariationPreviewModal({
  isOpen, onClose, title = '변형 결과 미리보기', idLabel = '등록 전',
  /* 승인되어 문제은행에 등록된 뒤에만 번호가 생긴다 — 그때는 머리의 번호가 그 문제로 가는
     링크가 된다(ProblemBlock 이 그린다). 등록 전에는 null 이고 idLabel 이 대신 선다. */
  problemId = null,
  bodyLexical = null, difficulty = null, conceptNames = [],
}) {
  const { getAccessToken } = useAuth();
  const [dict, setDict] = useState(null);
  const [html, setHtml] = useState(null); // null=아직, ''=본문 없음
  const [conceptDetail, setConceptDetail] = useState(null);
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  // 본문을 HTML 로 굳힌다. 변환기(에디터 노드 전체)는 창을 열 때 처음 받는다 —
  // 실패해도 창은 뜨고 본문 자리만 빈다(ProblemPreviewV2 와 같은 규칙).
  useEffect(() => {
    if (!isOpen) { setHtml(null); return; }
    const doc = parseDoc(bodyLexical);
    if (doc == null) { setHtml(''); return; }
    let alive = true;
    loadLexicalToHtml()
      .then((conv) => { if (alive && aliveRef.current) setHtml(conv(doc)); })
      .catch(() => { if (alive && aliveRef.current) setHtml(''); });
    // eslint-disable-next-line consistent-return
    return () => { alive = false; };
  }, [isOpen, bodyLexical]);

  useEffect(() => {
    if (!isOpen) return undefined;
    let alive = true;
    Promise.resolve(fetchConceptDictV2(getAccessToken))
      .then((d) => { if (alive) setDict(Array.isArray(d) ? d : null); })
      .catch(() => { if (alive) setDict(null); });
    return () => { alive = false; };
  }, [isOpen, getAccessToken]);

  const openConcept = useCallback(async (conceptId) => {
    if (conceptId == null) return;
    setConceptDetail({ loading: true, data: [] });
    const list = await fetchConceptDetailV2([conceptId], getAccessToken);
    setConceptDetail(list == null ? null : { loading: false, data: list });
  }, [getAccessToken]);

  useEffect(() => { if (!isOpen) setConceptDetail(null); }, [isOpen]);

  if (!isOpen) return null;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={(
        <span className="inline-flex items-baseline gap-1.5">
          {title}
          <span className="text-xs font-normal text-gray-400">등록 전</span>
        </span>
      )}
      size="md"
      maxHeight="90vh"
      className="md:!max-w-[480px]"
    >
      <ProblemBlock
        id={problemId}
        idLabel={idLabel}
        showNumbers={false}
        /* 번호가 없는 산출이라 이미 다 받은 상태로 넘긴다 — 이 부품은 상태를 받아 그리기만 한다 */
        state={{ status: 'ok', data: { difficulty, concept_names: conceptNames } }}
        dict={dict}
        html={html}
        onConceptClick={openConcept}
      />
      {conceptDetail && (
        <MathceptDetailModal
          isOpen
          onClose={() => setConceptDetail(null)}
          data={conceptDetail.data}
          loading={conceptDetail.loading}
        />
      )}
    </Modal>
  );
}

export default VariationPreviewModal;
